import { AccountInfo } from '@aibees/elephant/components/backoffice/account-info';
import { CurrentCompanyContactData } from '@aibees/elephant/components/current-company-contact-data';
import { Chip, Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Avatar, Divider, Link, Stack, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { ActionButton } from './components/action-button';
import { DialogManagerProvider } from './components/action-button/dialog-manager';
import { EditInformation } from './components/edit-information';
import * as Styled from './styles';
import * as SX from './sx';
import type { ContactLink, InformationCardProps as Props } from './types';

export const InformationCard = memo(
  ({ className, isUserCard, showAccountStatus, data }: Props) => {
    const [isMainContact, setIsMainContact] = useState(false);
    const links: ContactLink[] = [
      {
        icon: 'email-outline',
        value: '',
      },
      {
        icon: 'phone',
        value: '',
      },
      {
        icon: 'linkedin',
        value: '',
      },
      {
        icon: 'facebook',
        value: '',
      },
      {
        icon: 'twitter',
        value: '',
      },
      {
        icon: 'instagram',
        value: '',
      },
    ];

    return (
      <DialogManagerProvider>
        <Styled.Wrapper
          className={classnames('InformationCard', className)}
          sx={SX.root(isUserCard)}
        >
          <Stack direction="row" gap={0.5}>
            <Avatar src={data.imageSrc} size={isUserCard ? 56 : 80} />

            <Stack width="100%" gap={1}>
              {isUserCard ? (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="grey.700" fontWeight={600}>
                    {data.name}
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <ActionButton
                      isMainContact={isMainContact}
                      onSetMainContact={() => setIsMainContact(true)}
                      status={data.status}
                    />
                    <EditInformation disabled={data.status === 'PENDING'} />
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="body2" color="grey.700" fontWeight={600}>
                    {data.name}
                  </Typography>
                  <Chip
                    label="Owner"
                    size="small"
                    variant="outlined"
                    color="primary"
                    shape="rounded"
                  />
                  <Chip
                    label={isMainContact ? 'Main contact' : 'Not main contact'}
                    size="small"
                    variant="outlined"
                    color={isMainContact ? 'success' : 'warning'}
                    shape="rounded"
                  />
                </Stack>
              )}

              <Stack direction="row" gap={0.5}>
                {links.map((i) => (
                  <Link key={i.value} sx={{ lineHeight: 0 }}>
                    <Icon
                      name={i.icon}
                      sxx={SX.contactLink(i.disabled)}
                      size={15}
                    />
                  </Link>
                ))}
              </Stack>
              {isUserCard ? (
                <Stack direction="row" gap={1}>
                  <Chip
                    label="Owner"
                    size="small"
                    variant="outlined"
                    color="primary"
                    shape="rounded"
                  />
                  <Chip
                    label={isMainContact ? 'Main contact' : 'Not main contact'}
                    size="small"
                    variant="outlined"
                    color={isMainContact ? 'success' : 'warning'}
                    shape="rounded"
                  />
                </Stack>
              ) : (
                <Stack direction="row" gap={0.5}>
                  <ActionButton
                    isMainContact={isMainContact}
                    onSetMainContact={() => setIsMainContact(true)}
                    status={data.status}
                  />
                  <EditInformation />
                </Stack>
              )}
            </Stack>
          </Stack>

          {isUserCard && <Divider />}

          {(isUserCard || showAccountStatus) && (
            <Stack
              gap={showAccountStatus ? 1 : 2}
              pr={showAccountStatus ? 2.5 : 0}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body3" fontWeight={400} color="grey.500">
                  Account Status
                </Typography>
                <Chip
                  label={
                    data.status.charAt(0).toUpperCase() +
                    data.status.slice(1).toLowerCase()
                  }
                  size="small"
                  variant="outlined"
                  color={
                    data.status === 'ACTIVE'
                      ? 'success'
                      : data.status === 'PENDING'
                      ? 'warning'
                      : 'default'
                  }
                  shape="rounded"
                />
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body3" fontWeight={400} color="grey.500">
                  Sign up date
                </Typography>
                <Typography variant="body3" fontWeight={500} color="grey.700">
                  Mar 10, 2024 - 20:43
                </Typography>
              </Stack>
            </Stack>
          )}

          {!isUserCard && (
            <Stack sx={SX.companyInformation}>
              <AccountInfo
                name="Ai-bees.io"
                link=""
                logo="/assets/images/default-company-logo.svg"
                socialMedia={{
                  domain: 'https://ai-bees.io',
                  facebook: 'https://facebook.com/aibees',
                  linkedin: 'https://linkedin.com/company/aibees',
                }}
                isInformation
              />

              <CurrentCompanyContactData
                iconOrder="horizontal"
                hideDomains={true}
                size="medium"
              />
            </Stack>
          )}
        </Styled.Wrapper>
      </DialogManagerProvider>
    );
  }
);

InformationCard.displayName = 'InformationCard';
